import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { reviewsAPI } from '../utils/api';
import { toast } from 'sonner';

/**
 * Two-way review dialog. Triggered from a booking card after checkout.
 *
 *  - role === 'guest'  → guest reviews the location (target: location)
 *  - role === 'host'   → host reviews the guest (target: guest)
 *
 * After submit: refresh parent. Reviews are visible to the other side either
 * once both sides reviewed OR after 7 days (auto-publish job).
 */
const ReviewDialog = ({ open, onOpenChange, booking, onSubmitted }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!open || !booking) return;
    const load = async () => {
      try {
        setLoading(true);
        const response = await reviewsAPI.getBookingReviews(booking.id);
        setData(response.data);
        if (response.data.my_review) {
          setRating(response.data.my_review.rating);
          setComment(response.data.my_review.comment || '');
        } else {
          setRating(0);
          setComment('');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Nepodařilo se načíst recenze');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, booking, onOpenChange]);

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Vyberte hodnocení (1–5 hvězdiček)');
      return;
    }
    if (!comment.trim() || comment.trim().length < 10) {
      toast.error('Komentář musí mít alespoň 10 znaků');
      return;
    }

    try {
      setSubmitting(true);
      if (data.role === 'guest') {
        await reviewsAPI.createGuestReview({
          booking_id: booking.id,
          location_id: data.location_id,
          rating,
          comment: comment.trim(),
        });
      } else {
        await reviewsAPI.createHostReview({
          booking_id: booking.id,
          guest_id: data.guest_user_id,
          rating,
          comment: comment.trim(),
        });
      }
      toast.success('Děkujeme za hodnocení! ⭐️');
      onOpenChange(false);
      if (onSubmitted) onSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při odesílání hodnocení');
    } finally {
      setSubmitting(false);
    }
  };

  const isGuestRole = data?.role === 'guest';
  const title = isGuestRole ? 'Ohodnotit místo' : 'Ohodnotit hosta';
  const description = isGuestRole
    ? `Jak se vám líbil pobyt na lokaci "${booking?.location_name}"?`
    : `Jak se choval host při pobytu na "${booking?.location_name}"?`;
  const placeholder = isGuestRole
    ? 'Popište, co se vám líbilo nebo nelíbilo… (min. 10 znaků)'
    : 'Popište zkušenost s hostem… (min. 10 znaků)';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="review-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-center py-8 text-gray-500">Načítání…</p>
        ) : data?.my_review ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-green-800 mb-2">✅ Vaše hodnocení</p>
              <StarsRead value={data.my_review.rating} />
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{data.my_review.comment}</p>
            </div>
            {data.other_review && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-800 mb-2">
                  💬 Hodnocení od {data.other_review.reviewer_name}
                </p>
                <StarsRead value={data.other_review.rating} />
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{data.other_review.comment}</p>
              </div>
            )}
            {!data.other_review && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Hodnocení druhé strany bude zveřejněno po dokončení obou recenzí nebo automaticky po 7 dnech.
              </p>
            )}
          </div>
        ) : data?.can_review ? (
          <div className="space-y-5">
            <StarsPicker value={rating} onChange={setRating} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Komentář</label>
              <Textarea
                placeholder={placeholder}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                data-testid="review-comment-input"
              />
              <p className="text-xs text-gray-500 mt-1">{comment.length} / 1000 znaků</p>
            </div>
            <p className="text-xs text-gray-500">
              Vaše hodnocení bude zveřejněno, jakmile ohodnotí i druhá strana, nebo automaticky po 7 dnech.
            </p>
          </div>
        ) : (
          <p className="text-center py-8 text-gray-500">
            Pro tuto rezervaci nelze hodnocení odeslat (uplynula lhůta 7 dní, nebo rezervace byla zrušena).
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Zavřít
          </Button>
          {data?.can_review && !data?.my_review && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-review-btn"
            >
              {submitting ? 'Odesílám…' : 'Odeslat hodnocení'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StarsPicker = ({ value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Hodnocení</label>
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className="focus:outline-none transition-transform hover:scale-110"
          data-testid={`star-${s}`}
        >
          <Star className={`w-9 h-9 ${s <= value ? 'fill-orange-500 text-orange-500' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
    <p className="text-xs text-gray-500 mt-1">{value > 0 ? `${value} / 5` : 'Klikněte pro výběr'}</p>
  </div>
);

const StarsRead = ({ value }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`w-5 h-5 ${s <= value ? 'fill-orange-500 text-orange-500' : 'text-gray-300'}`} />
    ))}
  </div>
);

export default ReviewDialog;
